import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  MouseEvent,
} from "react";
import { FaChevronDown, FaMapMarkerAlt } from "react-icons/fa";
import {
  AiOutlineHeart,
  AiFillStar,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineClose,
} from "react-icons/ai";
import apiClient from "../api/Apiservice";
import { BASE_URL } from "../api/devprofile";
import { useLocation, useNavigate } from "react-router-dom";
import pgImage from "../assets/images/pgImage.png";
import logo from "../assets/images/logo.png";
import SignupModal from "./Signup";
import PriceSliderDropdown from "./PriceSliderDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import DatePickerDropdown from "./DatePickerDropdown";
import SortByDropdown from "./SortByDropdown";
import MapComponent from "./MapComponent";

// ----------------------------------------------------
// TYPES & INTERFACES
// ----------------------------------------------------
interface City {
  id: number;
  city: string;
  new: boolean;
  lat: number;
  lng: number;
}

interface PriceRange {
  min: number;
  max: number;
}

interface HeaderProps {
  selectedCity: string;
  setSelectedCity: React.Dispatch<React.SetStateAction<string>>;
}

interface NearbyLocality {
  sub_level: string;
}

interface NearbyLocalitiesProps {
  localitiesnear: NearbyLocality[];
  setLocality: React.Dispatch<React.SetStateAction<string>>;
}

interface Amenity {
  ameneties_id: string;
  ameneties_name: string;
  ameneties_image: string;
}

interface PillDropdownProps {
  label: string;
  items: Array<string | Amenity>;
  onSelect: (value: string | string[]) => void;
  multiSelect?: boolean;
  resetTrigger?: number;
}
interface FiltersProps {
  mapView: boolean;
  setMapView: React.Dispatch<React.SetStateAction<boolean>>;
  onFilterSelect: (category: string, value: any) => void;
  localitiesData: NearbyLocality[];
  ameneties: [];
  shareResponse: [];
  resetTrigger: number; // New prop to pass reset trigger to dropdowns
  totalItems: number;
}

interface Filter {
  category: string;
  value: any;
}

interface ActiveFiltersProps {
  activeFilters: Filter[];
  onRemoveFilter: (category: string) => void;
  onClearFilters: () => void;
}

interface ListingCardProps {
  title: string;
  location: string;
  distance: string;
  price: string;
  rating: number;
  gender: string;
  imageUrl: string;
}

interface CustomPaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

interface Property {
  propertyId: string;
  basic_details: {
    propertyname: string;
    houseArea: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
  lowestRent: number;
  overallRating: number;
  property_information: {
    pgType: string;
  };
  additional_information: {
    images: string[];
  };
}

interface ListingsProps {
  mapView: boolean;
  propertiesData: Property[];
  cityselected: string;
}

interface LocationState {
  selectedCity: string;
  locality: string;
}

// Marker type for MapComponent
interface MarkerType {
  id: number;
  lat: number;
  lng: number;
  pg_name: string;
  price: string;
  imageUrl: string;
  area: string;
}

// ----------------------------------------------------
// DATA & HELPER FUNCTIONS
// ----------------------------------------------------
const cities: City[] = [
  { id: 0, city: "Bengaluru", new: false, lat: 12.97194, lng: 77.59369 },
  { id: 1, city: "Chennai", new: false, lat: 13.08784, lng: 80.27847 },
  { id: 2, city: "Coimbatore", new: false, lat: 10.8, lng: 77.09 },
  { id: 3, city: "Delhi", new: false, lat: 28.7040592, lng: 77.1024902 },
  { id: 4, city: "Hyderabad", new: false, lat: 17.38405, lng: 78.45636 },
  { id: 5, city: "Kolkata", new: false, lat: 22.54111111, lng: 88.33777778 },
  { id: 6, city: "Mumbai", new: false, lat: 19.07283, lng: 72.88261 },
  { id: 7, city: "Noida", new: true, lat: 28.58, lng: 77.33 },
  { id: 8, city: "Pune", new: false, lat: 18.51957, lng: 73.85535 },
];

const formatFilterValue = (value: any): string => {
  if (Array.isArray(value)) {
    return value.join(", ");
  } else if (typeof value === "object" && value !== null) {
    if ("min" in value && "max" in value) {
      const range = value as PriceRange;
      return `${range.min} - ${range.max}`;
    }
    return Object.entries(value)
      .map(([key, val]) => `${key}: ${val}`)
      .join(", ");
  }
  return String(value);
};

const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// ----------------------------------------------------
// ICON CASTS TO AVOID TS2786 ERRORS
// ----------------------------------------------------
const ChevronIcon = FaChevronDown as React.FC<{
  size?: number;
  className?: string;
}>;
const CloseIcon = AiOutlineClose as React.FC<{
  size?: number;
  className?: string;
}>;
const HeartIcon = AiOutlineHeart as React.FC<{
  size?: number;
  className?: string;
}>;
const StarIcon = AiFillStar as React.FC<{ size?: number; className?: string }>;
const MapMarkerIcon = FaMapMarkerAlt as React.FC<{
  size?: number;
  className?: string;
}>;
const LeftIcon = AiOutlineLeft as React.FC<{
  size?: number;
  className?: string;
}>;
const RightIcon = AiOutlineRight as React.FC<{
  size?: number;
  className?: string;
}>;

// ----------------------------------------------------
// COMPONENTS
// ----------------------------------------------------
const Header: React.FC<HeaderProps> = ({ selectedCity, setSelectedCity }) => {
  const [isSignupOpen, setSignupOpen] = useState<boolean>(false);
  const { state } = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [cityDropdownOpen, setCityDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside as any);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside as any);
  }, []);

  const toggleCityDropdown = () => setCityDropdownOpen((prev) => !prev);

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    setCityDropdownOpen(false);
  };

  return (
    <>
      <header className="flex justify-between items-center h-25 px-6 bg-white shadow-md">
        <img src={logo} alt="zoy logo" className="h-24 w-auto object-contain" />
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={toggleCityDropdown}
            className="flex items-center space-x-2 rounded-full border border-gray-200 px-6 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200 text-[#0F172A] bg-white"
          >
            <span className="font-medium">{selectedCity}</span>
            <ChevronIcon
              size={14}
              className={`text-pink-500 transform transition-transform ${
                cityDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {cityDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <ul className="py-1">
                {cities.map((cityObj) => (
                  <li key={cityObj.id}>
                    <button
                      type="button"
                      onClick={() => handleCitySelect(cityObj.city)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 focus:outline-none"
                    >
                      {cityObj.city}
                      {cityObj.new && (
                        <span className="ml-2 text-xs text-red-500">New</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <nav className="space-x-4 flex items-center">
          <button
            onClick={() => setSignupOpen(true)}
            className="flex items-center text-sm font-medium text-black border border-black px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FontAwesomeIcon icon={faUser} className="mr-2 text-sm" /> Sign Up
          </button>
        </nav>
      </header>
      <SignupModal isOpen={isSignupOpen} onClose={() => setSignupOpen(false)} />
    </>
  );
};

const NearbyLocalities: React.FC<NearbyLocalitiesProps> = ({
  localitiesnear,
  setLocality,
}) => {
  return (
    <div className="max-w-screen-xl mx-auto my-6 px-4">
      <div className="bg-[#F9FBFD] rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Search in nearby localities
        </h2>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {localitiesnear.map((loc) => (
            <span
              key={loc.sub_level}
              className="inline-block rounded-full bg-white border border-gray-200 px-4 py-1.5 text-sm text-gray-700 shadow-sm hover:border-pink-400 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setLocality(loc.sub_level)}
            >
              {loc.sub_level}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const PillDropdown: React.FC<PillDropdownProps> = ({
  label,
  items,
  onSelect,
  multiSelect = false,
  resetTrigger,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Store selected values as strings (amenity names or simple strings)
  const [selectedValue, setSelectedValue] = useState<string | string[]>(
    multiSelect ? [] : ""
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside as any);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside as any);
  }, []);

  // Reset the internal state when resetTrigger changes
  useEffect(() => {
    setSelectedValue(multiSelect ? [] : "");
  }, [resetTrigger, multiSelect]);

  // Helper to extract a comparable value (string) from an item
  const getItemValue = (item: string | Amenity): string =>
    typeof item === "object" ? item.ameneties_name : item;

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (item: string | Amenity) => {
    const value = getItemValue(item);
    if (multiSelect) {
      setSelectedValue((prev) => {
        const current = prev as string[];
        return current.includes(value)
          ? current.filter((i) => i !== value)
          : [...current, value];
      });
    } else {
      setSelectedValue(value);
    }
  };

  const handleApply = () => {
    if (
      (multiSelect && (selectedValue as string[]).length > 0) ||
      (!multiSelect && selectedValue)
    ) {
      onSelect(selectedValue);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="inline-flex items-center space-x-1.5 rounded-md border border-[#E2E8F0] bg-white px-4 py-1.5 text-sm font-medium text-[#0F172A] hover:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-200 transition"
      >
        <span>{label}</span>
        <ChevronIcon
          size={12}
          className={`transform transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          } text-[#0F172A]`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 min-w-[400px] bg-white rounded-xl shadow-lg border border-gray-200 z-10 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">{label}</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {items.map((item) => {
              const itemValue = getItemValue(item);
              const isSelected = multiSelect
                ? (selectedValue as string[]).includes(itemValue)
                : selectedValue === itemValue;
              return (
                <button
                  key={typeof item === "object" ? item.ameneties_id : itemValue}
                  onClick={() => handleSelect(item)}
                  className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                    isSelected
                      ? "bg-pink-500 text-white border-pink-500"
                      : "border-gray-300 text-gray-700 hover:border-pink-500 hover:text-pink-500"
                  }`}
                >
                  {typeof item === "object" ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={item.ameneties_image}
                        alt={item.ameneties_name}
                        className="h-4 w-4"
                      />
                      <span>{item.ameneties_name}</span>
                    </div>
                  ) : (
                    <span>{item}</span>
                  )}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={handleApply}
            className="w-full bg-pink-500 text-white font-medium py-2 rounded-md hover:bg-pink-600 transition-colors"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

const Filters: React.FC<FiltersProps> = ({
  mapView,
  setMapView,
  onFilterSelect,
  localitiesData,
  ameneties,
  shareResponse,
  resetTrigger,
  totalItems,
}) => {
  console.log(shareResponse, "shareResponse");
  console.log(ameneties, "amenities");
  const filterLocalities = localitiesData.map((loc) => loc.sub_level);
  const filterAmeneties = ameneties.map((loc: any) => loc?.ameneties_name);
  const filterShareType = shareResponse.map((loc: any) => loc?.share_type);
  const filtersData = [
    { label: "Locality", items: [...filterLocalities] },
    { label: "Sharing", items: [...filterShareType] },
    { label: "Gender", items: ["Men", "Women", "Co-living", "Transgender"] },
    { label: "Amenities", items: [...ameneties] },
  ];

  const toggleMapView = () => setMapView((prev) => !prev);

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-4">
      <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
        {totalItems} PGs near you— find your perfect stay today!
      </h1>
      <div className="flex items-center justify-between flex-wrap">
        <div className="flex items-center flex-wrap gap-3">
          <PriceSliderDropdown
            label="Price"
            min={1}
            max={10000}
            onSelect={(value: any) => onFilterSelect("Price", value)}
          />
          <DatePickerDropdown
            label="Move-in & Move-out"
            onSelect={(dates: any) =>
              onFilterSelect("Move-in & Move-out", dates)
            }
          />
          {filtersData.map((filter) => (
            <PillDropdown
              key={filter.label}
              label={filter.label}
              items={filter.items}
              multiSelect={filter.label === "Amenities"}
              onSelect={(value) => onFilterSelect(filter.label, value)}
              resetTrigger={resetTrigger}
            />
          ))}
        </div>

        <div className="flex items-center space-x-6 mt-3 md:mt-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Map View</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={mapView}
                onChange={toggleMapView}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-pink-300 peer-checked:bg-pink-500 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
            </label>
          </div>

          <SortByDropdown
            onSelect={(value: string) => onFilterSelect("Sort", value)}
          />
        </div>
      </div>
    </div>
  );
};

const FilterChip: React.FC<{
  label: string;
  removable: boolean;
  onClick: () => void;
}> = ({ label, removable, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center space-x-1 border border-pink-500 text-pink-500 px-3 py-1.5 text-sm rounded-md hover:bg-pink-50 transition-colors"
    >
      <span>{label}</span>
      {removable && <CloseIcon size={12} />}
    </button>
  );
};

const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  activeFilters,
  onRemoveFilter,
  onClearFilters,
}) => {
  if (activeFilters.length === 0) return null;
  return (
    <div className="bg-white">
      <div className="max-w-screen-xl mx-auto px-4 py-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Active Filters
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-4">
            {activeFilters.map((filter) => (
              <div key={filter.category} className="flex flex-col items-center">
                <span className="text-xs text-gray-500">{filter.category}</span>
                <FilterChip
                  label={formatFilterValue(filter.value)}
                  removable={true}
                  onClick={() => onRemoveFilter(filter.category)}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={onClearFilters}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

const ListingCard: React.FC<ListingCardProps> = ({
  title,
  location,
  distance,
  price,
  rating,
  gender,
  imageUrl,
}) => {
  return (
    <div className="relative bg-white rounded-lg shadow-md overflow-hidden">
      <div className="relative w-full h-48">
        <img
          src={imageUrl}
          alt={title}
          className="object-cover w-full h-full"
        />
        <button
          type="button"
          className="absolute top-2 right-2 bg-white rounded-full p-2 shadow hover:bg-gray-100 transition"
        >
          <HeartIcon className="text-gray-600" size={18} />
        </button>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <StarIcon className="text-yellow-400" size={16} />
              <span className="text-sm text-gray-800 font-medium">
                {rating}
              </span>
            </div>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                gender === "Women"
                  ? "bg-pink-500 text-white"
                  : "bg-blue-500 text-white"
              }`}
            >
              {gender}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-600">{location}</p>
        {/* <div className="flex items-center space-x-1 text-sm text-gray-500">
          <MapMarkerIcon className="text-green-500" size={14} />
          <span>{distance || "N/A"}</span>
        </div> */}
        <p className="text-sm text-gray-800">
          Starts from{" "}
          <span className="text-blue-600 font-semibold">{price} / month</span>
        </p>
      </div>
    </div>
  );
};

const CustomPagination: React.FC<CustomPaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
}) => {
  const goToPreviousPage = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };
  const goToNextPage = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };
  return (
    <div className="flex items-center justify-center space-x-3 mt-6">
      <button
        type="button"
        onClick={goToPreviousPage}
        disabled={currentPage === 1}
        className="text-gray-700 disabled:text-gray-300 hover:text-pink-500 transition-colors"
      >
        <LeftIcon size={16} />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          className={`flex items-center justify-center w-8 h-8 text-sm font-medium rounded-full transition-colors ${
            page === currentPage
              ? "bg-gray-200 text-gray-800"
              : "text-gray-700 hover:text-pink-500"
          }`}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={goToNextPage}
        disabled={currentPage === totalPages}
        className="text-gray-700 disabled:text-gray-300 hover:text-pink-500 transition-colors"
      >
        <RightIcon size={16} />
      </button>
    </div>
  );
};

const Listings: React.FC<ListingsProps> = ({
  mapView,
  propertiesData,
  cityselected,
}) => {
  const navigate = useNavigate();
  const handlePropertyClick = (propertyId: string) => {
    const selectedProperty = propertiesData.find(
      (property) => property.propertyId === propertyId
    );
    const entries = Object.entries(propertiesData);
    const randomEntries = entries.sort(() => Math.random() - 0.5).slice(0, 3);
    const similarProperties = Object.fromEntries(randomEntries);

    if (selectedProperty) {
      navigate(`/propertyDetails`, {
        state: { selectedProperty, similarProperties, cityselected },
      });
    } else {
      console.error("Property not found:", propertyId);
    }
  };

  return (
    <div>
      <div
        className={`grid gap-6 ${
          mapView ? "grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {propertiesData.map((property, idx) => (
          <div
            key={idx}
            className="bg-white rounded-lg shadow-md cursor-pointer"
            onClick={() => handlePropertyClick(property.propertyId)}
          >
            <ListingCard
              title={property.basic_details.propertyname}
              location={property.basic_details.houseArea}
              distance={""}
              price={`₹${property.lowestRent}`}
              rating={property.overallRating}
              gender={property.property_information.pgType}
              imageUrl={
                property.additional_information.images &&
                property.additional_information.images.length > 0 &&
                property.additional_information.images[0] !== "null"
                  ? property.additional_information.images[0]
                  : pgImage
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ----------------------------------------------------
// MAIN PROPERTIES COMPONENT
// ----------------------------------------------------
function Properties() {
  const locationState = useLocation().state as LocationState;
  const [mapView, setMapView] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [selCity, setSelCity] = useState<string>(locationState.selectedCity);
  const [totalItems, setTotalItems] = useState<number>(0);
  const totalPages = Math.ceil(totalItems / 100);
  const [locality, setLocality] = useState<string>(locationState.locality);

  // Data & Pagination states
  const [propertiesData, setPropertiesData] = useState<Property[]>([]);
  const [markerList, setMarkerList] = useState<MarkerType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [localities, setLocalities] = useState<NearbyLocality[]>([]);
  const [ameneties, setAmeneties] = useState<[]>([]);
  const [shareResponse, setShareResponse] = useState<[]>([]);

  // Filtering states (example: start/end date)
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  const defaultImageUri = require("../assets/images/logo.png");

  // Reset locality when city changes
  useEffect(() => {
    setLocality("");
  }, [selCity]);

  // Reset properties data and pagination when filters or city/locality change
  useEffect(() => {
    setPropertiesData([]);
    setPageNumber(1);
    setHasMoreData(true);
  }, [activeFilters, selCity, locality]);

  useEffect(() => {
    fetchProperties();
  }, [selCity, activeFilters, locality, pageNumber, startDate, endDate]);

  // Fetch properties API call
  const fetchProperties = useCallback(async () => {
    if (isLoading || !hasMoreData) return;

    setIsLoading(true);
    try {
      const filtering: any = {
        amenities: [],
        pgType: "",
        startDate: formatDateTime(startDate),
        endDate: formatDateTime(endDate),
        shareType: "",
        location: "",
        priceRange: { min: 0, max: 0 },
        selectedCity: selCity,
        sorting: "",
        page_size: 1000,
        page_number: pageNumber,
        commonSearch: locality,
      };

      activeFilters.forEach((filter) => {
        switch (filter.category) {
          case "Locality":
            filtering.location = filter.value;
            break;
          case "Price":
            if (
              typeof filter.value === "object" &&
              (filter.value as PriceRange).min !== undefined &&
              (filter.value as PriceRange).max !== undefined
            ) {
              filtering.priceRange = filter.value;
            }
            break;
          case "Sharing":
            filtering.shareType = filter.value;
            break;
          case "Gender":
            filtering.pgType = filter.value;
            break;
          case "Amenities":
            if (Array.isArray(filter.value)) {
              filtering.amenities = filter.value;
            } else {
              filtering.amenities.push(filter.value);
            }
            break;
          case "Sort":
            filtering.sorting = filter.value;
            break;
          default:
            break;
        }
      });

      const requestBody = { filtering };
      console.log(requestBody, ":::::requestBody");
      const { data } = await apiClient.post(
        `${BASE_URL}/zoy_customer/allproperties`,
        requestBody
      );

      console.log(data.length, "::::::::::data.length");
      if (data.totalItems !== undefined) {
        setTotalItems(data.totalItems);
      } else {
        setTotalItems(data.length);
      }
      console.log(JSON.stringify(data, null, 2), "::::::::::data");
      console.log(data.totalItems, "::::::::::data.totalItems");

      setPropertiesData((prev) =>
        pageNumber === 1 ? data : [...prev, ...data]
      );
      setHasMoreData(data.length > 0);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    pageNumber,
    selCity,
    activeFilters,
    startDate,
    endDate,
    locality,
    isLoading,
    hasMoreData,
  ]);

  // Generate markers including the required 'area' property.
  const openMaps = async () => {
    try {
      const markerPromises = propertiesData.map((prop, index) => {
        return {
          id: index,
          lat: prop.basic_details.location.latitude,
          lng: prop.basic_details.location.longitude,
          pg_name: prop.basic_details.propertyname,
          price: prop.lowestRent.toString(), // convert to string
          imageUrl:
            (prop.additional_information?.images &&
              prop.additional_information.images[0]) ||
            defaultImageUri,
          area: prop.basic_details.houseArea,
        };
      });

      const markersList = await Promise.all(markerPromises);
      setMarkerList(markersList);
    } catch (error) {
      console.error("Error fetching markers:", error);
    }
  };

  // Fetch localities API call
  const fetchLocalities = useCallback(async () => {
    const { data, status } = await apiClient.get(
      `${BASE_URL}/zoy_customer/zoy_common/browserLocality/${selCity}`
    );
    console.log(data,":::::::::::browserLocality data")
    if (status === 200) {
      setLocalities(data);
    }
  }, [selCity]);

  const fetchAmeneties = useCallback(async () => {
    const { data, status } = await apiClient.get(
      `${BASE_URL}/zoy_common/ameneties`
    );
    if (status === 200) {
      setAmeneties(data);
    }
  }, [selCity]);

  const fetchshareResponse = useCallback(async () => {
    const { data, status } = await apiClient.get(
      `${BASE_URL}/zoy_common/shareType`
    );
    if (status === 200) {
      setShareResponse(data);
    }
  }, [selCity]);

  useEffect(() => {
    fetchLocalities();
  }, [selCity]);

  useEffect(() => {
    fetchAmeneties();
  }, [selCity]);

  useEffect(() => {
    fetchshareResponse();
  }, [selCity]);

  useEffect(() => {
    fetchProperties();
  }, [selCity]);

  useEffect(() => {
    openMaps();
  }, [propertiesData]);

  // Active Filters Handlers
  const handleFilterSelect = (category: string, value: any) => {
    setActiveFilters((prev) => {
      const others = prev.filter((filter) => filter.category !== category);
      return [...others, { category, value }];
    });
  };

  const handleRemoveFilter = (category: string) => {
    setActiveFilters((prev) =>
      prev.filter((filter) => filter.category !== category)
    );
  };

  // New state to trigger reset for PillDropdown components
  const [resetFilterTrigger, setResetFilterTrigger] = useState<number>(0);

  const handleClearFilters = () => {
    setActiveFilters([]);
    setResetFilterTrigger((prev) => prev + 1);
  };

  const selectedCityCoords = cities.find((city) => city.city === selCity);
  const initialCenter = selectedCityCoords
    ? { lat: selectedCityCoords.lat, lng: selectedCityCoords.lng }
    : { lat: 12.97194, lng: 77.59369 };
console.log(activeFilters,"::::::::::activeFilters")
  return (
    <div className="bg-gray-50">
      <Header selectedCity={selCity} setSelectedCity={setSelCity} />
      <main className="container mx-auto px-4 py-8">
        <Filters
          mapView={mapView}
          setMapView={setMapView}
          onFilterSelect={handleFilterSelect}
          localitiesData={localities}
          ameneties={ameneties}
          shareResponse={shareResponse}
          resetTrigger={resetFilterTrigger}
          totalItems={totalItems}
        />
        <ActiveFilters
          activeFilters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={handleClearFilters}
        />
        <div className={`flex flex-col ${mapView ? "md:flex-row" : ""} gap-6`}>
          <div className={`${mapView ? "md:w-2/3" : "w-full"}`}>
            <Listings
              mapView={mapView}
              propertiesData={propertiesData}
              cityselected={selCity}
            />
          </div>
          {mapView && (
            <div className="bg-white rounded-lg shadow-md md:w-1/3 md:h-[350px]">
              <MapComponent markerList={markerList} initialCenter={initialCenter}/>
            </div>
          )}
        </div>
        <CustomPagination
          totalPages={totalPages}
          currentPage={pageNumber}
          onPageChange={(page: number) => setPageNumber(page)}
        />
        <NearbyLocalities
          localitiesnear={localities}
          setLocality={setLocality}
        />
      </main>
    </div>
  );
}

export default Properties;
